import * as Mui from "@mui/material";
import {Button, Card, CardActions, CardContent, Grid, IconButton, Stack, Tab, Tabs, Typography} from "@mui/material";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import FormTextField from "../../../experiments/forms/FormTextField";
import {useTranslation} from "react-i18next";
import {SubmitHandler, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";
import {useParams} from "react-router-dom";
import dayjs from "dayjs";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import * as Const from "./components/filters/PaymentRequestConstants";
import {
    checkCorrelationID,
    checkCorrelationName, handleStatus,
    STATUS_FOR_PBL_TYPE_B,
    STATUS_FOR_PBL_TYPE_J
} from "./components/filters/PaymentRequestConstants";
import FormDatePicker from "../../../experiments/forms/FormDatePicker";
import {usePaymentRequestRepository} from "../../../api/repositories/PaymentRequestRepository";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {formatCurrency} from "../../../utils/FormatUtils";
import DownloadRoundedIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import useToggle from "./hooks/useToggle";
import EditPaymentRequest from "./EditPaymentRequest";
import EditAmount from "./EditAmount";
import {STATE_MODAL_UPDATING} from "./atoms/PaymentRequestAtoms";
import {useRecoilState} from "recoil";
import {
    OUTSTANDING_ACCOUNTED,
    OUTSTANDING_SUSPEND,
    PAYMENT_REQUEST_ACCOUNTED,
    PAYMENT_REQUEST_ADD_AMOUNT,
    PAYMENT_REQUEST_ADD_NOTE,
    PAYMENT_REQUEST_DOWNLOAD_XML, PAYMENT_REQUEST_IMMEDIATE_RETRY,
    PAYMENT_REQUEST_OUTSTANDING, PAYMENT_REQUEST_PAY_BY_LINK,
    PAYMENT_REQUEST_RESTORE,
    PAYMENT_REQUEST_RETRY,
    PAYMENT_REQUEST_SUSPEND
} from "../../../utils/FinanceScope";
import {useHasScope} from "../../../../../config/globalConfig";
import FormDateTimePicker from "../../../experiments/forms/FormDateTimePicker";
import UnpaidHistory from "./UnpaidHistory";
import FinanceDialog from "../../../component/FinanceDialog";
import {InfoRounded} from "@mui/icons-material";
import {TabPanel} from "../../../utils/TabPanel";
import {PaymentRequestsLinked} from "./PaymentRequestsLinked";
import {Box} from "@mui/system";


function PaymentRequestDetailWizard() {
    const {t} = useTranslation("finance", {keyPrefix: "payments.payment-request"});
    const {
        getPaymentsRequestsById,
        putPaymentsRequestsById,
        downloadXML,
        checkPayByLink,
        createPayByLink,
        retryPayment
    } = usePaymentRequestRepository()
    const hasScope = useHasScope;
    const {id} = useParams();
    const [detail, setDetail] = useState<any>();
    const [showEditNotes, toggleEditNote] = useToggle();
    const [showEditAmount, toggleEditAmount] = useToggle();
    const [immediateRetryModal, setImmediateRetryModal] = useState<boolean>(false);
    const {dispatchGlobalData} = useContext(GlobalContext);
    const [updating, setUpdating] = useRecoilState(STATE_MODAL_UPDATING);
    const [openPblModal, setOpenPblModal] = useState<boolean>(false);
    const [openCheckPblModal, setOpenCheckPblModal] = useState<boolean>(false);
    const [actionType, setActionType] = useState<string>('');

    const schema = Yup.object().shape({});

    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };


    useEffect(() => {
        if (id !== undefined) {
            getPaymentsRequestsById(id).then((response) => {
                let data = response.result;
                setDetail(data);
                setValue('paymentType', data?.type ? t(Const.convertTypology(Const.paymentTypes, data?.type)) : "");
                setValue('amount', data.amount ? formatCurrency(data.amount, "EUR", 0.01) : "");
                setValue('amountType', data?.amountType ? t(Const.convertAmountType(Const.amountTypes, data.amountType, data.usePreAuth)) : "");
                setValue('status', data?.status ? convertStatus(data) : "");
                setValue('dueDate', data.dueDate);
                setValue('modifiedDate', data.modifiedDate);
                setValue('id', data.id);
                setValue('invoiceDate', data.invoiceDate);
                setValue('sapInvoiceNumber', data.sapInvoiceNumber);
                setValue('invoiceNumber', data.invoiceNumber);
                setValue('customerId', data.customerId);
                setValue('customer', data.customerName);
                setValue('causal', getReason(data.reason));
                setValue('ra', data.raCode);
                setValue('paymentMode', data.cardTerminalType);
                setValue('termsOfPayment', data.invoiceSchema);
                setValue('cardNetwork', data.cardNetwork);
                setValue('cardTerminalId', data.cardTerminalId);
                setValue('serviceItem', data.serviceItems);
                setValue('note', data.note);
                setValue('maskeredPan', data.maskeredPan);
                setValue('lastTransactionId', data.lastTransactionId);
                setValue('lastTransactionDate', data.lastTransactionDate);
                setValue('amountRecovered', getAmountSummary(data));
                setValue('preauthAmountReleased', data.preauthAmountReleased ? formatCurrency(data.preauthAmountReleased, "EUR", 0.01) : "");
                setValue('correlationName', checkCorrelationName({data, value: data.correlationName}, t));
                setValue('correlationId', checkCorrelationID({data, value: data.correlationId}, t));
            })
        }
        if (updating) setUpdating(false);
    }, [id, t, updating])

    function getAmountSummary(data: any) {
        let amount = data.amountRecovered ? formatCurrency(data.amountRecovered, "EUR", 0.01) : "";
        let amountSummary = amount ? (amount + "   " + data.amountRecoveredDescr) : "";
        return amountSummary.length > 60 ? amountSummary.substring(0, 60) + '...' : amountSummary;
    }

    function convertStatus(data: any) {
        let status = handleStatus(data);
        return t('statuses.' + status);
    }

    function getReason(reason: any) {
        if (reason !== undefined && reason !== "" && (reason.includes("refund.") || reason.includes("payment.") || reason.includes("Pay-by-Link"))) {
            return t('errors.payment.' + reason);
        } else {
            return reason;
        }
    }

    const defaultValues = {
        paymentType: "",
        amount: "",
        amountType: "",
        status: "",
        dueDate: "",
        invoiceDate: "",
        sapInvoiceNumber: "",
        invoiceNumber: "",
        customerId: "",
        customer: "",
        causal: "",
        ra: "",
        paymentMode: "",
        termsOfPayment: "",
        cardNetwork: "",
        cardTerminalId: "",
        serviceItem: "",
        sddReceipt: "",
        note: "",
        amountRecovered: "",
        id: "",
        modifiedDate: "",
        lastTransactionId: "",
        lastTransactionDate: "",
        maskeredPan: "",
        preauthAmountReleased: "",
        correlationName: "",
        correlationId: ""
    };

    const {control, setValue, handleSubmit} = useForm<any>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema)
    });

    const onSubmit: SubmitHandler<any> = () => {

    }


    return (
        <Box>
            {showEditNotes &&
                <EditPaymentRequest
                    open={showEditNotes}
                    onClose={toggleEditNote}
                    id={id}
                    note={detail.note}

                />
            }
            {showEditAmount &&
                <EditAmount
                    open={showEditAmount}
                    onClose={toggleEditAmount}
                    id={id}
                    amountRecovered={detail.amountRecovered}
                    amountRecoveredDescr={detail.amountRecoveredDescr}
                />
            }
            <Card variant="outlined" sx={{margin: '15px'}}>
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{padding: '15px'}}>
                    <Typography component="div" variant={'body1'} fontWeight={'bold'} textTransform={'uppercase'}>
                        {t('pages.wizard.form.title-arrow', {id: id})}
                    </Typography>
                </Stack>


                <Card variant="outlined" sx={{margin: '15px'}}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="Payment Request Tabs">
                        <Tab label={t('pages.wizard.form.title')}/>
                        <Tab label={t('pages.wizard.form.title-linked')}/>
                    </Tabs>
                    <TabPanel value={tabValue} index={0}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <CardContent>
                                <Grid container direction={'row'} rowGap={5}>
                                    <Grid container item spacing={2} alignItems={'center'}>
                                        <Grid item md={3} sm={12} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'paymentType'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelPaymentType'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={12} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'amount'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelAmount'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'amountType'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelAmountType'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'status'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelStatus'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>

                                        <Grid item md={3} sm={12} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'preauthAmountReleased'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelPreAuthReleasedAmount'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={12} xs={12}>
                                            <FormDatePicker
                                                control={control}
                                                name={'invoiceDate'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelInvoiceDate'),
                                                    maxDate: dayjs().toDate(),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'sapInvoiceNumber'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelSapInvoiceNumber'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'invoiceNumber'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelInvoiceNumber'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>

                                        <Grid container item spacing={2} alignItems={'center'}>
                                            <Grid item md={3} sm={3} xs={6}>
                                                <FormDatePicker
                                                    control={control}
                                                    name={'dueDate'}
                                                    props={{
                                                        disabled: true,
                                                        label: t('pages.wizard.form.labelDueDate'),
                                                        maxDate: dayjs().toDate(),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={3} sm={3} xs={6}>
                                                <FormTextField
                                                    control={control}
                                                    name={'causal'}
                                                    props={{
                                                        disabled: true,
                                                        label: t('pages.wizard.form.labelCausal'),
                                                        sx: {width: '100%'},
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={6} sm={6} xs={12}>
                                                <FormTextField
                                                    control={control}
                                                    name={'ra'}
                                                    props={{
                                                        disabled: true,
                                                        label: t('pages.wizard.form.labelRa'),
                                                        sx: {width: '100%'},
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid container item spacing={2} alignItems={'center'}>
                                            <Grid item md={3} sm={6} xs={12}>
                                                <FormTextField
                                                    control={control}
                                                    name={'customerId'}
                                                    props={{
                                                        disabled: true,
                                                        label: t('pages.wizard.form.labelCustomerId'),
                                                        sx: {width: '100%'},
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={3} sm={6} xs={12}>
                                                <FormTextField
                                                    control={control}
                                                    name={'customer'}
                                                    props={{
                                                        disabled: true,
                                                        label: t('pages.wizard.form.labelCustomer'),
                                                        sx: {width: '100%'},
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={6} sm={6} xs={12}>
                                                <FormTextField
                                                    control={control}
                                                    name={'serviceItem'}
                                                    props={{
                                                        disabled: true,
                                                        label: t('pages.wizard.form.labelServiceItem'),
                                                        sx: {width: '100%'},
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'paymentMode'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelPaymentMode'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'termsOfPayment'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelTermsOfPayment'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'cardNetwork'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelCardNetwork'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'cardTerminalId'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelCardTerminalId'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormDateTimePicker
                                                control={control}
                                                name={'modifiedDate'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelModificationDate'),
                                                    maxDate: dayjs().toDate(),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'lastTransactionId'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.lastTransactionId'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormDateTimePicker
                                                control={control}
                                                name={'lastTransactionDate'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.lastTransactionDate'),
                                                    maxDate: dayjs().toDate(),
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'maskeredPan'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.maskeredPan'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'id'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.labelId'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'correlationName'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.correlationName'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>
                                        <Grid item md={3} sm={6} xs={12}>
                                            <FormTextField
                                                control={control}
                                                name={'correlationId'}
                                                props={{
                                                    disabled: true,
                                                    label: t('pages.wizard.form.correlationId'),
                                                    sx: {width: '100%'},
                                                }}
                                            />
                                        </Grid>

                                        <Grid container item spacing={2} alignItems={'center'}>

                                            <>
                                                <Grid item md={5.5} sm={5.5} xs={5.5}>
                                                    <FormTextField
                                                        control={control}
                                                        name={'note'}
                                                        props={{
                                                            disabled: true,
                                                            label: t('pages.wizard.form.note'),
                                                            sx: {width: '100%'},
                                                        }}
                                                    />
                                                </Grid>
                                                {(detail?.status && detail.status !== Const.ID_FILTER_OUTSTANDING) && hasScope(PAYMENT_REQUEST_ADD_NOTE) &&
                                                    <Grid item md={0.5} sm={0.5} xs={0.5}>
                                                        <IconButton color="accent" onClick={() => openEditModalNotes()}>
                                                            <EditIcon/>
                                                        </IconButton>
                                                    </Grid>
                                                }

                                            </>

                                            {hasScope(PAYMENT_REQUEST_ADD_AMOUNT) && (
                                                <>
                                                    <Grid item md={5.5} sm={5.5} xs={5.5}>
                                                        <FormTextField
                                                            control={control}
                                                            name={'amountRecovered'}
                                                            props={{
                                                                disabled: true,
                                                                label: t('pages.wizard.form.amount'),
                                                                sx: {width: '100%'},
                                                            }}
                                                        />
                                                    </Grid>
                                                    {(detail?.status && detail?.status === Const.ID_FILTER_SUSPENDED && !detail?.isOutstanding) && (
                                                        <Grid item md={0.5} sm={0.5} xs={0.5}>
                                                            <IconButton color="accent"
                                                                        onClick={() => openEditModalAmount()}>
                                                                <EditIcon/>
                                                            </IconButton>
                                                        </Grid>)}
                                                </>
                                            )}
                                        </Grid>
                                    </Grid>
                                    <Grid container item spacing={2} alignItems={'center'}>
                                        <UnpaidHistory updating={updating}/>
                                    </Grid>
                                </Grid>
                            </CardContent>
                            <CardActions sx={{justifyContent: 'flex-end'}}>
                                {showCardActions()}
                            </CardActions>
                        </form>
                        <FinanceDialog open={immediateRetryModal}
                                       onClose={() => setImmediateRetryModal(false)}
                                       callback={() => {
                                           retryPaymentRequest(detail?.id, true)
                                       }}
                                       titleIcon={<InfoRounded style={{color: 'accent.main'}}/>}
                                       title={t('pages.immediate-retry.title')}
                                       content={t('pages.immediate-retry.content')}
                                       confirmButton={t('modal.actionButtons.confirm')}
                                       cancelButton={t('modal.actionButtons.cancel')}
                        />
                        <FinanceDialog open={openCheckPblModal}
                                       onClose={() => setOpenCheckPblModal(false)}
                                       callback={() => {
                                           putRequest(actionType);
                                           setUpdating(true);
                                           window.scrollTo(0, 0)
                                       }}
                                       titleIcon={<InfoRounded style={{color: 'accent.main'}}/>}
                                       title={t('modal.pblActiveTitle')}
                                       content={t('modal.pblActiveContent')}
                                       confirmButton={t('modal.actionButtons.confirm')}
                                       cancelButton={t('modal.actionButtons.cancel')}
                        />
                        <FinanceDialog open={openPblModal}
                                       onClose={() => setOpenPblModal(false)}
                                       callback={() => {
                                           createPayByLink(detail?.id, detail?.dealer, detail?.legalEntity).then((res) => {
                                               if (res) {
                                                   dispatchGlobalData({
                                                       type: setSnackbar,
                                                       payload: {
                                                           snackbar: {
                                                               message: t('modal.pblSuccess'),
                                                               type: snackbarTypes.success,
                                                               show: true,
                                                               autoClose: true
                                                           }
                                                       }
                                                   });
                                                   setUpdating(true);
                                                   window.scrollTo(0, 0);
                                               } else {
                                                   dispatchGlobalData({
                                                       type: setSnackbar,
                                                       payload: {
                                                           snackbar: {
                                                               message: t('modal.pblError'),
                                                               type: snackbarTypes.error,
                                                               show: true,
                                                               autoClose: true
                                                           }
                                                       }
                                                   });
                                               }
                                           }).catch(() => {
                                                  dispatchGlobalData({
                                                    type: setSnackbar,
                                                    payload: {
                                                         snackbar: {
                                                              message: t('modal.pblError'),
                                                              type: snackbarTypes.error,
                                                              show: true,
                                                              autoClose: true
                                                         }
                                                    }
                                                  });
                                             });
                                       }}
                                       titleIcon={<InfoRounded style={{color: 'accent.main'}}/>}
                                       title={t('modal.pblTitle')}
                                       content={t('modal.pblTitleContent')}
                                       confirmButton={t('modal.actionButtons.confirm')}
                                       cancelButton={t('modal.actionButtons.cancel')}
                        />

                    </TabPanel>
                    <TabPanel value={tabValue} index={1}>
                        <PaymentRequestsLinked paymentRequest={detail} hasScope={hasScope}/>
                    </TabPanel>
                </Card>
            </Card>
        </Box>
    );

    function showCardActions() {
        let status = detail?.status;
        let alreadySentToSap = detail?.alreadySentToSap;

        let showImmediateRetry = (status === Const.ID_FILTER_UNPAID) && hasScope(PAYMENT_REQUEST_IMMEDIATE_RETRY)
        let showOutstanding = !alreadySentToSap && status === Const.ID_FILTER_UNPAID && hasScope(PAYMENT_REQUEST_OUTSTANDING)
        let showRestore = ((!alreadySentToSap && status === Const.ID_FILTER_SUSPENDED && !detail?.isOutstanding) || (!alreadySentToSap && status === Const.ID_FILTER_ACCOUNTED && !detail?.isOutstanding)) && hasScope(PAYMENT_REQUEST_RESTORE)
        let showRetry = !alreadySentToSap && status === Const.ID_FILTER_UNPAID && hasScope(PAYMENT_REQUEST_RETRY)
        let showDownload = alreadySentToSap && status === Const.ID_FILTER_ACCOUNTED && hasScope(PAYMENT_REQUEST_DOWNLOAD_XML)
        let showSuspended = ((status === Const.ID_FILTER_UNPAID || status === Const.ID_FILTER_TO_RETRY) || (status === Const.ID_FILTER_TO_SEND && detail?.type === 'B')) && hasScope(PAYMENT_REQUEST_SUSPEND) && hasScope(OUTSTANDING_SUSPEND);
        let showClose = (status === Const.ID_FILTER_UNPAID || status === Const.ID_FILTER_TO_RETRY || (status === Const.ID_FILTER_SUSPENDED && !detail?.isOutstanding) || (status === Const.ID_FILTER_TO_SEND && detail?.type === 'B')) && hasScope(PAYMENT_REQUEST_ACCOUNTED) && hasScope(OUTSTANDING_ACCOUNTED);
        let showPbl = enablePbl() && hasScope(PAYMENT_REQUEST_PAY_BY_LINK);

        return (
            <>
                {showImmediateRetry &&
                    <Button color={"accent"} size={"small"}
                            onClick={() => immediateRetry()}>{t('pages.wizard.buttons.immediateRetry')}</Button>}
                {showOutstanding &&
                    <Button color={"accent"} size={"small"}
                            onClick={() => putRequest('outstanding')}>{t('pages.wizard.buttons.outstanding')}</Button>}
                {showRestore &&
                    <Button color={"accent"} size={"small"}
                            onClick={() => putRequest('restore')}>{t('pages.wizard.buttons.restore')}</Button>}
                {showRetry &&
                    <Button color={"accent"} size={"small"}
                            onClick={() => retryPaymentRequest(detail.id, false)}>{t('pages.wizard.buttons.retry')}</Button>}
                {showSuspended &&
                    <Button color={"accent"} size={"small"}
                            onClick={() => putRequestWithCheck('suspend')}>{t('pages.wizard.buttons.suspend')}</Button>}
                {showClose &&
                    <Button color={"accent"} size={"small"}
                            onClick={() => putRequestWithCheck('accounted')}>{t('pages.wizard.buttons.close')}</Button>}
                {showDownload &&
                    <Button color={"accent"} size={"small"} endIcon={<DownloadRoundedIcon/>}
                            onClick={downloadFileXML}>{t('pages.wizard.buttons.download-xml')}</Button>}
                {showPbl &&
                    <Button color={"accent"} size={"small"}
                            onClick={() => payByLink()}>{t('pages.wizard.buttons.payByLink')}</Button>
                }

            </>
        )
    }

    function openEditModalNotes(): void {
        toggleEditNote();
    }

    function openEditModalAmount(): void {
        toggleEditAmount();
    }

    function enablePbl(): boolean {
        return detail?.amountType === 'PAY' && ((STATUS_FOR_PBL_TYPE_J.includes(detail?.status) && detail?.type === 'J') || (STATUS_FOR_PBL_TYPE_B.includes(detail?.status) && detail?.type === 'B') && (detail?.lastTransactionId === null || detail?.lastTransactionId === undefined));
    }

    function immediateRetry() {
        setImmediateRetryModal(true);
    }


    function downloadFileXML() {
        downloadXML(detail?.id).then((res) => {
            if (res) {
                const dataURI = 'data:application/xml;charset=utf-8;base64,' + encodeURI(res);
                const downloadLink = document.createElement('a');
                downloadLink.href = dataURI;
                downloadLink.download = "payment-request" + detail?.id + ".xml";
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
            }
        });
    }

    function payByLink() {
        setOpenPblModal(true);
    }

    function putRequestWithCheck(type: string) {
        checkPayByLink(detail?.id)
            .then((res) => {
                if (res && res.status === 'ACTIVE') {
                    setActionType(type);
                    setOpenCheckPblModal(true);
                } else if(res && res.status === 'INTERACTIVE_INSERT'){
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('snackbar.in-progress-pay-by-link'),
                                type: snackbarTypes.warning,
                                show: true,
                                autoClose: true
                            }
                        }
                    });
                } else {
                    putRequest(type);
                }
            })
    }

    function putRequest(type: string) {
        putPaymentsRequestsById(detail?.id, type, "", detail?.dealer, detail?.legalEntity).then((res) => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.success-change-state'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });
            setUpdating(true);
            window.scrollTo(0, 0)
        }).catch(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.error-change-state'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        });
    }

    function retryPaymentRequest(id: string, immediate: boolean) {
        retryPayment(id, immediate).then((res: any) => {
            if (res.response !== "") {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('snackbar.success-change-state'),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true,
                            autoHideDuration: 5000
                        }
                    }
                });
                setUpdating(true);
                window.scrollTo(0, 0);
            } else {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('snackbar.error-change-state'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            }
        }).catch((error:any) => {
            let errorMessage = error?.data?.errorMessage !== "" && (error?.data?.errorMessage).includes('PBL interactive in progress') ? t('snackbar.retry-pay-by-link') : t('snackbar.error-change-state');
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: errorMessage,
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        });
    }
}

export default PaymentRequestDetailWizard;

